// Connector health + manual sync. Progress for running syncs is patched in from sync.progress
// WebSocket events; completion triggers a refetch.
import { useEffect, useMemo, useRef, useState } from 'react';
import { formatValue, relativeTime } from '../../lib/format';
import { FreshnessBadge, StatusBadge } from '../../ui/Badges';
import { Icon } from '../../ui/Icon';
import { Modal } from '../../ui/Modal';
import { useToast } from '../../ui/Toasts';
import { errorMessage } from '../../app/api';
import type { RegistryEntry, WidgetComponentProps } from '../types';

const MODE: Record<string, string> = { webhook: 'Webhooks + polling + reconciliation', incremental: 'Polling + reconciliation', scheduled: 'Scheduled' };

function every(sec: number | null) {
  if (!sec) return '';
  if (sec >= 86_400) return `every ${Math.round(sec / 86_400)}d`;
  if (sec >= 3600) return `every ${Math.round(sec / 3600)}h`;
  return `every ${Math.round(sec / 60)}m`;
}

function SyncPanel({ data, props, ctx }: WidgetComponentProps) {
  const toast = useToast();
  const actions: any[] = props.actions ?? [];
  const can = (id: string) => actions.find((a) => a.id === id);
  const [confirm, setConfirm] = useState<{ row: any } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Patch progress from streamed events.
  const live = useMemo(() => {
    const m = new Map<string, { progress: number; rows: number; status: string; stage?: string | null }>();
    for (const e of ctx.events) {
      const p = e.payload ?? {};
      const key = `${p.connector}:${p.storeId ?? 'global'}`;
      if (e.event === 'sync.progress') m.set(key, { progress: p.progress, rows: p.rowsProcessed, status: 'running', stage: p.stage });
      if (e.event === 'sync.started' || e.event === 'sync.queued') m.set(key, { progress: 0, rows: 0, status: e.event === 'sync.started' ? 'running' : 'queued' });
      if (['sync.completed', 'sync.failed', 'sync.cancelled'].includes(e.event)) m.delete(key);
    }
    return m;
  }, [ctx.events]);
  const lastFinish = useRef(0);
  useEffect(() => {
    const done = ctx.events.filter((e) => ['sync.completed', 'sync.failed', 'sync.cancelled'].includes(e.event)).length;
    if (done !== lastFinish.current) {
      lastFinish.current = done;
      ctx.refetch();
    }
  }, [ctx.events, ctx]);

  const run = async (action: string, row: any, input: any) => {
    setBusy(`${action}:${row?.key ?? ''}`);
    try {
      const res = await ctx.action(action, null, input);
      toast(res.message ?? 'Done', 'success');
      ctx.refetch();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  };

  const groups = new Map<string, any[]>();
  for (const c of data.connectors ?? []) groups.set(c.connectorId, [...(groups.get(c.connectorId) ?? []), c]);

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      {[...groups.entries()].map(([id, rows]) => {
        const first = rows[0];
        return (
          <section className="sync-group" key={id} aria-label={first.label}>
            <div className="sync-group-head">
              <strong>{first.label}</strong>
              <span className="muted" style={{ fontSize: 12 }}>
                {MODE[first.mode] ?? first.mode} {every(first.pollIntervalSec)} · API {first.apiVersion}
              </span>
            </div>
            {rows.map((r) => {
              const lv = live.get(`${r.connectorId}:${r.storeId ?? 'global'}`);
              const running = lv ?? (r.running ? { progress: r.running.progress, rows: r.running.rowsProcessed, status: r.running.status, stage: r.running.stage } : null);
              return (
                <div className="sync-row" key={r.key}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.storeName}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {r.connectorId === 'hmrc' ? 'Monthly rates for every sales month' : `${formatValue(r.rows, 'integer')} orders since ${r.coverageStart ? r.coverageStart.slice(0, 10) : '—'}`}
                    </div>
                  </div>
                  <div>
                    <StatusBadge status={running ? running.status : r.status} />
                  </div>
                  <div>
                    {running ? (
                      <div style={{ display: 'grid', gap: 3 }}>
                        <div className="progress" role="progressbar" aria-valuenow={Math.round(running.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                          <div style={{ width: `${Math.max(3, running.progress * 100)}%` }} />
                        </div>
                        <span className="muted" style={{ fontSize: 11 }}>
                          {running.stage ? `${running.stage} · ` : ''}
                          {Math.round(running.progress * 100)}%{running.rows ? ` · ${formatValue(running.rows, 'integer')} rows` : ''}
                        </span>
                      </div>
                    ) : r.freshness && r.freshness.status !== 'fresh' ? (
                      <FreshnessBadge freshness={r.freshness} />
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>Last sync {relativeTime(r.lastSuccessAt)}</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {r.last?.error && !running && r.status !== 'completed' ? <span className="error-text">{r.last.error}</span> : r.nextRunAt ? `Next ${relativeTime(r.nextRunAt)}` : ''}
                  </div>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    {running && can('cancel') && r.running?.syncId && (
                      <button className="btn sm" onClick={() => run('cancel', r, { syncId: r.running.syncId })}>
                        Cancel
                      </button>
                    )}
                    {!running && r.manualSync && can('trigger') && (
                      <button className="btn sm" disabled={busy !== null} onClick={() => run('trigger', r, { connector: r.connectorId, storeId: r.storeId })}>
                        <Icon name="refresh" size={13} /> Sync now
                      </button>
                    )}
                    {!running && r.manualSync && can('resync') && r.connectorId !== 'hmrc' && (
                      <button className="btn sm ghost" disabled={busy !== null} onClick={() => setConfirm({ row: r })}>
                        Re-sync
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}
      {confirm && (
        <Modal
          title="Full re-sync"
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button className="btn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  const r = confirm.row;
                  setConfirm(null);
                  void run('resync', r, { connector: r.connectorId, storeId: r.storeId });
                }}
              >
                Re-sync
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            {can('resync')?.confirm} <br />
            <span className="muted">
              {confirm.row.label} · {confirm.row.storeName}
            </span>
          </p>
        </Modal>
      )}
    </div>
  );
}

export const syncPanel: RegistryEntry = {
  component: SyncPanel,
  isEmpty: (d) => !d?.connectors?.length,
  toTable: (d) => ({
    columns: [
      { key: 'label', label: 'Connector' },
      { key: 'storeName', label: 'Store' },
      { key: 'status', label: 'Status', format: 'status' },
      { key: 'lastSuccessAt', label: 'Last success', format: 'datetime' },
      { key: 'rows', label: 'Rows', format: 'integer', numeric: true },
    ],
    rows: d?.connectors ?? [],
  }),
};
